<template>
  <template v-if="drawerOptions?.desktopRedesign && isDesktop">
    <Slideout
      :id="sortFilterSlideOutId"
      title="Filter"
      position="right"
      :sticky-header="true"
      handle-top-layer="show"
      :disable-teleport="true"
      class="optimazed-facets"
      @close-slideout="closeSlideout"
    >
      <div :class="{ 'facetMask': isFacetsLoading }">
        <Spinner
          v-if="isFacetsLoading"
          class="facetLoader"
        />
        <div class="hide-for-large">
          <SortByCriteria />
        </div>
        <div :class="[preventAnimation && 'facets-prevent-animation']">
          <DeselectionPills :drawer="true" />
          <Accordion
            ref="accordionEl"
            :active-index="accordionActiveIndex"
            size="medium"
            @tab-open="onFacetTabToggle"
            @tab-close="onFacetClose"
          >
            <AccordionTab
              v-for="(tab, index) in accordionTabs"
              :key="tab?.facet?.name"
              :pt="accordionTabPtOptions"
              :header="tab?.header"
              :sub-text="selectedValuesListString(tab?.facet)"
            >
              <CriteriaWrapper
                v-if="accordionActiveIndex === index"
                :facet="tab?.facet"
                :active="accordionActiveIndex === index"
              />
            </AccordionTab>
          </Accordion>

          <div
            class="padding-top-m padding-bottom-m"
          >
            <button
              v-if="!facetRowsExpanded"
              class="text-button see-more-filters"
              @click="toggleFacetRows(true)"
            >
              See more filters
            </button>
          </div>
        </div>
      </div>

      <template #footer>
        <div class="grid-x actions-row sticky-redesign">
          <div class="actions-row-column">
            <button
              class="secondary button expanded"
              :disabled="isClearButtonDisabled || isFacetsLoading"
              @click.prevent="clearAllSelectedFacets"
            >
              Clear
            </button>
          </div>
          <div class="actions-row-column">
            <button
              class="primary button expanded"
              @click.prevent="applyFilter"
            >
              View {{ isMcom ? formattedSlideoutProductCount : formattedItemCount }} items
            </button>
          </div>
        </div>
      </template>
    </Slideout>
  </template>
  <template v-else>
    <Slideout
      :id="sortFilterSlideOutId"
      :title="rightSideDrawer ? 'Sort & filter' : accordionTabs[accordionActiveIndex]?.header"
      :position="rightSideDrawer || isMobileInLandscape ? 'right' : 'bottom'"
      :has-half-modal-exp="!rightSideDrawer"
      :sticky-header="true"
      handle-top-layer="show"
      :class="['optimazed-facets', halfModalSlideout, halfModalSlideoutPadding]"
      @close-slideout="closeSlideout"
    >
      <div :class="[{ 'facetMask': isFacetsLoading }, sizeDisplay]">
        <div
          v-if="rightSideDrawer"
          class="grid-x actions-row sticky-buttons"
        >
          <div class="actions-row-column">
            <button
              class="secondary button expanded"
              :disabled="isClearButtonDisabled"
              @click.prevent="clearAllSelectedFacets"
            >
              Clear
            </button>
          </div>
          <div class="actions-row-column">
            <button
              class="primary button expanded"
              @click.prevent="applyFilter"
            >
              View {{ formattedItemCount }} items
            </button>
          </div>
        </div>

        <template v-if="rightSideDrawer">
          <DeselectionPills
            :drawer="true"
            :facet="accordionTabs[accordionActiveIndex]?.facet"
          />
        </template>

        <template v-if="rightSideDrawer">
          <SortByCriteria />
        </template>

        <p
          class="large margin-top-m margin-bottom-s"
          :class="drawerOptions?.hideFilterBy ? 'hide' : ''"
        >
          Filter By
        </p>

        <div
          :class="[
            preventAnimation && 'facets-prevent-animation',
            marginTop
          ]"
        >
          <template v-if="rightSideDrawer">
            <Accordion
              ref="accordionEl"
              :active-index="accordionActiveIndex"
              size="medium"
              @tab-open="onFacetTabToggle"
              @tab-close="onFacetClose"
            >
              <AccordionTab
                v-for="(tab, index) in accordionTabs"
                :key="tab?.facet?.name"
                :header="tab?.header"
                :pt="accordionTabPtOptions"
                :sub-text="selectedValuesListString(tab?.facet)"
              >
                <CriteriaWrapper
                  v-if="accordionActiveIndex === index"
                  :facet="tab?.facet"
                  :active="accordionActiveIndex === index"
                />
              </AccordionTab>
            </Accordion>
          </template>
          <template v-else>
            <CriteriaWrapper
              :key="criteriaFacet"
              :facet="criteriaFacet"
              :is-dynamic="isDynamicFilters"
              :active="true"
            />
          </template>

          <div
            v-if="rightSideDrawer"
            :class="padding"
          >
            <button
              v-if="!facetRowsExpanded"
              class="text-button see-more-filters"
              @click="toggleFacetRows(true)"
            >
              See more filters
            </button>
          </div>
        </div>
        <Spinner
          v-if="isFacetsLoading"
          class="facetLoader"
        />
      </div>
    </Slideout>
  </template>
</template>

<script setup>
import { defineAsyncComponent, computed } from 'vue';
import { Accordion, AccordionTab } from '@atomic-ui/accordion';
import { Slideout } from '@atomic-ui/slideout';
import Spinner from '@atomic-ui/spinner';
import useEnvParams from '@composables/useEnvParams';
import useSortAndFilter from '../composables/useSortAndFilter';
import SortByCriteria from './SortByCriteria';
import CriteriaWrapper from './criteria/CriteriaWrapper';

const DeselectionPills = defineAsyncComponent(() => import('./criteria/DeselectionPills'));

const {
  sortFilterSlideOutId,
  closeSlideout,
  isClearButtonDisabled,
  clearAllSelectedFacets,
  applyFilter,
  accordionActiveIndex,
  accordionTabs,
  accordionEl,
  selectedValuesListString,
  drawerOptions,
  isDesktop,
  isFacetsLoading,
  formattedSlideoutProductCount,
  onFacetTabToggle,
  onFacetClose,
  preventAnimation,
  accordionTabPtOptions,
  toggleFacetRows,
  facetRowsExpanded,
  rightSideDrawer,
  isMobileInLandscape,
  halfModalSlideout,
  halfModalSlideoutPadding,
  sizeDisplay,
  marginTop,
  padding,
  isDynamicFilters,
  criteriaFacet,
} = useSortAndFilter();

const { isMcom } = useEnvParams();

const formattedItemCount = computed(() => (+(formattedSlideoutProductCount.value.replace(/[.,\s]/g, '')) > 500 ? '500+' : formattedSlideoutProductCount.value));

</script>

<style lang="scss" scoped>
@import "~@core/csg/src/scss/components/text-button-mixin.scss";
@include csg-text-button;

.see-more-filters {
  text-decoration: underline;
  font-weight: 700;
  padding: 0;
}
:deep(.accordion-header-sub-text) {
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  margin-top: 4px;
}
.facets-prevent-animation {
  :deep(.p-toggleable-content) {
    transition: none !important;
  }
}
:deep(.slideout-container .slideout) {
  overflow: visible;
}

.sticky-buttons {
  position: sticky;
  padding-bottom: rem-calc(20);
  top: rem-calc(90);
  z-index: 30;
  background: $white;
}

.actions-row {
  grid-gap: rem-calc(15px);

  .actions-row-column {
    width: calc(50% - rem-calc(7.5px));
  }
}

.sticky-redesign {
  position: sticky;
  bottom: 0;
}

.size-display {
  position: sticky;
  :deep(.checkBoxesContainer) {
    min-height: 237px;
  }
}

.facetMask {
  width: 100%;
  pointer-events: none;
  opacity: 0.5;
  z-index: 2;
  background-color: #fff;
}
.facetLoader {
  position: absolute;
  top: 50%;
  left: 50%;
}

:deep(.p-accordion-tab .p-accordion-content:has(> .sizes-dropdown)) {
  padding: 0;
}

:deep(.p-accordion-tab .p-accordion-content:has(> .color-facet)) {
  padding-bottom: 4px;
}
</style>

<style lang="scss" brand="bcom" scoped>
.see-more-filters {
  text-transform: none;
  letter-spacing: 0.14px;
}

:deep(.p-accordion-tab .p-accordion-content:has(> .color-facet)) {
  padding-bottom: 16px;
}

:deep(.sizes-dropdown + .facetContainer) {
  padding-bottom: 2px;
}

.sticky-buttons {
  @include breakpoint(medium down) {
    top: rem-calc(88);
  }
}
</style>

<style lang="scss">
@import 'src/scss/mixins';

.slideout-container {

  .slideout {
    scrollbar-width: thin;
  }

  .slideout-body {
    @include customScrollbar();
    margin-right: 2px;
  }

  .slideout-footer.slideout-footer-buttons:has(.actions-row) {
    padding-bottom: 1rem;
    z-index: 100;
  }
}

.optimazed-facets {
  .slideout-scroll-hidden {
    overflow-y: clip;
  }
}

.slideout-container.optimazed-facets .slideout,
.optimazed-facets + .slideout-container .slideout {
  @include breakpoint(medium up) {
    width: rem-calc(500);
  }
}

</style>
